import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const SNOW_COLORS = ["#c7d6ec", "#b8c9e4", "#dbe7f6"];
const CONFETTI_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6"];
const LEAF_COLORS = ["#d97706", "#b45309", "#65a30d", "#92400e"];
const FIRE_COLORS = ["#60a5fa", "#f59e0b", "#f472b6", "#a78bfa", "#34d399"];

function useReducedMotion() {
  return useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
}

function EffectCanvas({ effect }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let W = (canvas.width = canvas.offsetWidth);
    let H = (canvas.height = canvas.offsetHeight);
    const onResize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    window.addEventListener("resize", onResize);
    const R = (a, b) => a + Math.random() * (b - a);
    const pick = (arr) => arr[(Math.random() * arr.length) | 0];
    let parts = [];
    let rockets = [];
    let tick = 0;
    const spawn = {
      snow: () => ({ x: R(0, W), y: -10, r: R(1.6, 3.6), v: R(0.5, 1.4), sway: R(0, 6.28), swayV: R(0.008, 0.025), c: pick(SNOW_COLORS) }),
      leaves: () => ({ x: R(0, W), y: -14, w: R(6, 11), h: R(4, 7), v: R(0.7, 1.6), rot: R(0, 6.28), rotV: R(-0.03, 0.03), sway: R(0, 6.28), swayV: R(0.015, 0.035), c: pick(LEAF_COLORS) }),
      confetti: () => ({ x: R(0, W), y: -12, w: R(5, 9), h: R(3, 6), v: R(1.1, 2.4), rot: R(0, 6.28), rotV: R(-0.08, 0.08), c: pick(CONFETTI_COLORS) }),
    }[effect];
    const burst = (x, y) => {
      const c = pick(FIRE_COLORS);
      for (let i = 0; i < 52; i++) {
        const a = (Math.PI * 2 * i) / 52 + R(-0.05, 0.05);
        const s = R(1.4, 4.4);
        parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, decay: R(0.01, 0.02), c });
      }
    };
    const frame = () => {
      ctx.clearRect(0, 0, W, H);
      tick++;
      if (effect === "fireworks") {
        if (tick % 58 === 0) rockets.push({ x: R(W * 0.12, W * 0.88), y: H, vy: R(-9, -6.8), c: pick(FIRE_COLORS) });
        rockets = rockets.filter((r) => {
          r.y += r.vy; r.vy += 0.07;
          ctx.globalAlpha = 0.9; ctx.fillStyle = r.c; ctx.fillRect(r.x - 1, r.y - 4, 2.5, 6);
          if (r.vy >= -1) { burst(r.x, r.y); return false; }
          return true;
        });
        parts = parts.filter((p) => {
          p.x += p.vx; p.y += p.vy; p.vy += 0.045; p.life -= p.decay;
          if (p.life <= 0) return false;
          ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.c;
          ctx.beginPath(); ctx.arc(p.x, p.y, 1.8, 0, 6.283); ctx.fill();
          return true;
        });
      } else if (spawn) {
        if (parts.length < 70 && Math.random() < 0.55) parts.push(spawn());
        parts = parts.filter((p) => {
          p.y += p.v;
          if (effect === "snow") { p.sway += p.swayV; p.x += Math.sin(p.sway) * 0.5; }
          else { p.rot += p.rotV; p.x += Math.sin((effect === "leaves" ? (p.sway += p.swayV) : p.y * 0.02)) * (effect === "leaves" ? 1.1 : 0.4); }
          if (p.y > H + 16) return false;
          ctx.globalAlpha = 0.9;
          if (effect === "snow") {
            ctx.fillStyle = p.c; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill();
          } else {
            ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
            ctx.fillStyle = p.c; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
          }
          return true;
        });
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, [effect]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" data-testid={`campaign-effect-${effect}`} />;
}

function CssEffect({ effect }) {
  const balloons = useMemo(
    () => Array.from({ length: 9 }, (_, i) => ({
      left: 4 + i * 11 + Math.random() * 4,
      delay: i * 1.6, dur: 9 + (i % 4),
      color: ["#3b82f6", "#f59e0b", "#8b5cf6", "#10b981", "#ec4899"][i % 5],
      light: ["#bfdbfe", "#fde68a", "#ddd6fe", "#a7f3d0", "#fbcfe8"][i % 5],
    })),
    []
  );
  const bats = useMemo(
    () => Array.from({ length: 5 }, (_, i) => ({ top: 8 + i * 13, delay: i * 3.4, dur: 11 + i, size: 22 + (i % 3) * 8 })),
    []
  );
  const embers = useMemo(
    () => Array.from({ length: 14 }, (_, i) => ({ left: Math.random() * 100, delay: Math.random() * 8, dur: 6 + Math.random() * 5 })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid={`campaign-effect-${effect}`}>
      <style>{`
        @keyframes camp-balloon { 0% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-75vh) rotate(2.5deg); } 100% { transform: translateY(-125vh) rotate(-2deg); } }
        @keyframes camp-bat { 0% { transform: translateX(-12vw) translateY(0); } 25% { transform: translateX(22vw) translateY(-3vh); } 50% { transform: translateX(52vw) translateY(2vh); } 75% { transform: translateX(78vw) translateY(-2.5vh); } 100% { transform: translateX(112vw) translateY(0); } }
        @keyframes camp-ember { 0% { transform: translateY(8vh); opacity: 0; } 20% { opacity: 0.9; } 100% { transform: translateY(-75vh); opacity: 0; } }
      `}</style>
      {effect === "balloons" && balloons.map((b, i) => (
        <div key={`balloon-${i}`} className="absolute" style={{ left: `${b.left}%`, bottom: "-20vh", animation: `camp-balloon ${b.dur}s linear ${b.delay}s infinite` }}>
          <div style={{ width: 34, height: 44, borderRadius: "50% 50% 50% 50% / 56% 56% 44% 44%", background: `radial-gradient(circle at 32% 28%, ${b.light}, ${b.color})`, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
          <div style={{ width: 6, height: 6, background: b.color, clipPath: "polygon(50% 0, 0 100%, 100% 100%)", margin: "-2px auto 0" }} />
          <div style={{ width: 1, height: 42, background: "rgba(24,24,27,0.3)", margin: "0 auto" }} />
        </div>
      ))}
      {effect === "halloween" && (
        <>
          {bats.map((b, i) => (
            <svg key={`bat-${i}`} viewBox="0 0 32 12" className="absolute" style={{ top: `${b.top}%`, left: 0, width: b.size, animation: `camp-bat ${b.dur}s linear ${b.delay}s infinite` }}>
              <path d="M0 8 Q5 0 10 6 Q13 2 16 5 Q19 2 22 6 Q27 0 32 8 Q26 6 21 9 Q16 12 11 9 Q6 6 0 8 Z" fill="#18181b" opacity="0.85" />
            </svg>
          ))}
          {embers.map((e, i) => (
            <div key={`ember-${i}`} className="absolute rounded-full" style={{ left: `${e.left}%`, bottom: 0, width: 4, height: 4, background: "#f59e0b", boxShadow: "0 0 6px 1px rgba(245,158,11,0.6)", animation: `camp-ember ${e.dur}s linear ${e.delay}s infinite` }} />
          ))}
        </>
      )}
    </div>
  );
}

function Countdown({ target }) {
  const [t, setT] = useState(() => new Date(target).getTime() - Date.now());
  useEffect(() => {
    const id = setInterval(() => setT(new Date(target).getTime() - Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (t <= 0) {
    return <p className="mt-3 text-xs font-bold uppercase tracking-widest text-green-700" data-testid="campaign-live-note">It&rsquo;s live, right now</p>;
  }
  const cells = [
    ["Days", Math.floor(t / 86400000)],
    ["Hours", Math.floor(t / 3600000) % 24],
    ["Mins", Math.floor(t / 60000) % 60],
    ["Secs", Math.floor(t / 1000) % 60],
  ];
  return (
    <div className="flex justify-center gap-2 mt-3" data-testid="campaign-countdown">
      {cells.map(([label, v]) => (
        <div key={label} className="bg-zinc-950 text-white rounded-md px-2.5 py-1.5 min-w-[54px]">
          <p className="font-display text-lg font-black leading-none tabular-nums" data-testid={`campaign-countdown-${label.toLowerCase()}`}>{String(v).padStart(2, "0")}</p>
          <p className="text-[9px] font-semibold uppercase tracking-widest text-zinc-400 mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}

export default function CampaignLayer() {
  const [campaign, setCampaign] = useState(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    api.get("/campaigns/active").then((r) => setCampaign(r.data.campaign || null)).catch(() => {});
  }, []);
  if (!campaign) return null;
  const effect = reduced ? "none" : (campaign.effect || "none");
  const ctaExternal = (campaign.cta_url || "").startsWith("http");
  return (
    <div data-testid="campaign-layer">
      {effect !== "none" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {["snow", "confetti", "leaves", "fireworks"].includes(effect)
            ? <EffectCanvas effect={effect} />
            : <CssEffect effect={effect} />}
        </div>
      )}
      <div className="absolute bottom-5 inset-x-0 flex justify-center px-4 pointer-events-none z-10">
        <div className="pointer-events-auto glass-deep rounded-lg px-5 sm:px-7 py-4 text-center max-w-xl shadow-lg" data-testid="campaign-banner">
          <p className="font-display text-lg sm:text-xl font-black text-zinc-950 tracking-tight" data-testid="campaign-headline">{campaign.headline}</p>
          {campaign.subtext && <p className="text-xs sm:text-sm text-zinc-600 mt-1" data-testid="campaign-subtext">{campaign.subtext}</p>}
          {campaign.countdown_target && <Countdown target={campaign.countdown_target} />}
          {campaign.cta_label && campaign.cta_url && (
            ctaExternal ? (
              <a href={campaign.cta_url} data-testid="campaign-cta"
                className="inline-flex items-center gap-1.5 mt-3 h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                {campaign.cta_label}
              </a>
            ) : (
              <Link to={campaign.cta_url} data-testid="campaign-cta"
                className="inline-flex items-center gap-1.5 mt-3 h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                {campaign.cta_label}
              </Link>
            )
          )}
        </div>
      </div>
    </div>
  );
}
